import React from "react";

import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { formatCurrency } from "~/utils/helpers";

interface CreditUtilizationCardProps {
  clientCreditData: {
    utilized: number;
    available: number;
    percentage: number;
    insuredAmount: number;
  };
  orderAmount: number;
  orderAmountWithIva?: number;
  exceedsAvailableCredit: boolean;
}

export const CreditUtilizationCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-300 bg-white p-5 shadow-sm">
    <div className="mb-3 flex items-center gap-2">
      <Skeleton className="h-5 w-5 rounded-full" />
      <Skeleton className="h-5 w-44" />
    </div>
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-12" />
      </div>
      <Skeleton className="h-2 w-full rounded" />
      <div className="mt-2 grid grid-cols-2 gap-4">
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
      </div>
    </div>
  </div>
);

export const CreditUtilizationCard: React.FC<CreditUtilizationCardProps> = ({
  clientCreditData,
  orderAmount,
  orderAmountWithIva,
  exceedsAvailableCredit,
}) => {
  const displayAmount = orderAmountWithIva || orderAmount;
  const hasIva = !!orderAmountWithIva;

  return (
    <div className="rounded-xl border border-gray-300 bg-white p-5 shadow-sm">
      <h3 className="mb-3 flex items-center text-lg font-semibold text-gray-800">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="mr-2 h-5 w-5 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        Utilización de Crédito
      </h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Utilización</span>
          <span
            className={`text-sm font-bold ${clientCreditData.percentage > 80 ? "text-red-600" : "text-green-600"}`}
          >
            {clientCreditData.percentage.toFixed(1)}%
          </span>
        </div>

        <div className="relative pt-1">
          <div className="flex h-2 overflow-hidden rounded bg-gray-200 text-xs">
            <div
              style={{
                width: `${Math.min(clientCreditData.percentage, 100)}%`,
              }}
              className={`flex flex-col justify-center whitespace-nowrap text-center text-white shadow-none ${
                clientCreditData.percentage > 90
                  ? "bg-red-500"
                  : clientCreditData.percentage > 70
                    ? "bg-amber-500"
                    : "bg-green-500"
              }`}
            ></div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-4">
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Crédito Disponible</p>
            <p className="text-base font-semibold">
              {formatCurrency(clientCreditData.available)}
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Límite de Crédito</p>
            <p className="text-base font-semibold">
              {formatCurrency(clientCreditData.insuredAmount)}
            </p>
          </div>
        </div>

        {exceedsAvailableCredit && (
          <Alert className="mt-2 border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-800">
              El monto de esta nota ({formatCurrency(displayAmount)})
              {hasIva ? " con IVA" : ""} excede el crédito disponible (
              {formatCurrency(clientCreditData.available)}).
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
